"use client";
import { useUploadPolicy } from "@/features/uploads/use-upload-policy"

import { useEffect, useEffectEvent, useId, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { AnimatePresence, LayoutGroup, MotionConfig, motion, useAnimationFrame, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import type { MotionValue } from "motion/react";
import { ArrowUp, Mic, Pause, Play, Square, Trash2 } from "lucide-react";
import { motionTokens } from "@/components/ui/animated/lib/motion-tokens";
import styles from "./voice-recorder.module.css";
import { useVoiceCapture, useVoicePlayback } from "./voice-audio";
import { Avatar } from "../avatar/avatar";

export interface VoiceMessage {
  id: string;
  /** Length in seconds. */
  duration: number;
  /** Bar heights between 0 and 1. */
  peaks: number[];
  /** Sender name for incoming messages. Leave empty for your own. */
  from?: string;
  /** Sender portrait for incoming messages. */
  avatar?: string;
  /** Short line under the bubble, such as "9:41 AM". */
  meta?: string;
  audioUrl?: string;
  blob?: Blob;
}

/** Arc's voice composer and thread. Demo recording is the default; microphone mode captures local audio and passes the Blob through onSend. */
export interface VoiceRecorderProps {
  /** Stops capture/playback when its containing pane becomes hidden, retaining the take for review. */
  active?: boolean;
  defaultMessages?: VoiceMessage[];
  onSend?: (message: VoiceMessage) => void | Promise<void>;
  /** Called when a take is discarded. */
  onCancel?: () => void;
  /** Seconds before recording stops on its own. */
  maxDuration?: number;
  /** Meta line on messages you send. */
  sentMeta?: string;
  label?: string;
  className?: string;
  composerOnly?: boolean;
  composerClassName?: string;
  recordingMode?: "demo" | "microphone";
}

type Phase = "idle" | "recording" | "review";

const STEP = 90;
const PITCH = 5;
const BAR = 3;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
const grain = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };

/** A deterministic, speech like level for a sample: syllables inside phrases, with short pauses between words. */
export function voiceLevel(index: number, seed = 0) {
  const t = index + seed * 97;
  const phrase = .55 + .45 * Math.sin(t * .05 + seed);
  const syllable = Math.abs(Math.sin(t * .55 + Math.sin(t * .13) * 1.7));
  const pause = Math.sin(t * .085 + seed * 1.3) > .8 ? .14 : 1;
  return clamp((.22 + .78 * syllable) * phrase * pause * (.62 + .38 * grain(t)), .07, 1);
}
const barsFor = (seconds: number) => Math.round(clamp(14 + seconds * 1.3, 18, 36));
/** Downsamples the same levels the live waveform drew, so a sent bubble looks like what was recorded. */
export function voicePeaks(seed: number, seconds: number, bars = barsFor(seconds)) {
  const samples = Math.max(bars, Math.round(seconds * 1000 / STEP));
  return Array.from({ length: bars }, (_, bar) => {
    const from = Math.floor(bar * samples / bars), to = Math.max(from + 1, Math.floor((bar + 1) * samples / bars));
    let peak = 0;
    for (let index = from; index < to; index++) peak = Math.max(peak, voiceLevel(index, seed));
    return Math.round(peak * 100) / 100;
  });
}

/** Paints the live waveform: each sample enters at the right edge, grows to its level, and scrolls left at a constant speed. */
function paint(canvas: HTMLCanvasElement | null, elapsed: number, seed: number, stepped: boolean, levels?: number[]) {
  if (!canvas) return;
  const width = canvas.clientWidth, height = canvas.clientHeight;
  if (!width || !height) return;
  const ratio = window.devicePixelRatio || 1;
  if (canvas.width !== Math.round(width * ratio)) { canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio); }
  const context = canvas.getContext("2d");
  if (!context) return;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);
  context.fillStyle = getComputedStyle(canvas).color;
  const time = stepped ? Math.floor(elapsed / STEP) * STEP : elapsed;
  const newest = Math.floor(time / STEP);
  const visible = Math.ceil(width / PITCH) + 2;
  for (let i = newest; i > newest - visible && i >= 0; i--) {
    const age = time - i * STEP;
    const x = width - 2 - (age / STEP) * PITCH;
    if (x < -BAR) break;
    const grow = stepped ? 1 : clamp(age / 140, 0, 1);
    const h = Math.max(BAR, (levels ? levels[i] ?? .07 : voiceLevel(i, seed)) * (height - 4) * grow);
    // Older samples fade toward the left edge so the bar reads as time passing.
    context.globalAlpha = clamp(x / (width * .25), .25, 1);
    context.beginPath();
    context.roundRect(x, (height - h) / 2, BAR, h, BAR / 2);
    context.fill();
  }
  context.globalAlpha = 1;
}

function Bars({ peaks, progress, className }: { peaks: number[]; progress: MotionValue<number>; className?: string }) {
  const clip = useTransform(progress, p => `inset(0 ${(1 - clamp(p, 0, 1)) * 100}% 0 0)`);
  const bars = peaks.map((peak, i) => <span key={i} style={{ height: `${Math.max(12, peak * 100)}%` }} />);
  return <span className={[styles.bars, className].filter(Boolean).join(" ")} aria-hidden="true">
    <span className={styles.barsLayer}>{bars}</span>
    <motion.span className={styles.barsLayer} data-played style={{ clipPath: clip }}>{bars}</motion.span>
  </span>;
}

/** Plays a take on a clock: returns the progress value, whether it is playing, and a toggle. */
function usePlayback(duration: number, audioUrl?: string) {
  return useVoicePlayback(duration, audioUrl);
}

export function VoiceMessageBubble({ message, layoutId, as: Tag = "li" }: { message: VoiceMessage; layoutId?: string; as?: "li" | "div" }) {
  const { progress, playing, toggle, seek: seekAudio, error } = usePlayback(message.duration, message.audioUrl);
  const [now, setNow] = useState(0);
  useEffect(() => progress.on("change", p => setNow(Math.floor(p * message.duration))), [progress, message.duration]);
  const mine = !message.from;
  const label = `${playing ? "Pause" : "Play"} voice message${message.from ? ` from ${message.from}` : ""}, ${clock(message.duration)}`;

  function seek(event: ReactPointerEvent<HTMLSpanElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    seekAudio(clamp((event.clientX - rect.left) / rect.width, 0, 1));
  }
  return <Tag className={styles.message} data-mine={mine || undefined}>
    {!mine && (message.avatar
      ? <Avatar name={message.from ?? "Danaro"} src={message.avatar} size="sm" className={styles.avatar} />
      : <Avatar name={message.from ?? "Danaro"} size="sm" className={styles.avatar} />)}
    <div className={styles.messageBody}>
      <motion.div layoutId={layoutId} className={styles.bubble} style={{ borderRadius: 22 }} transition={motionTokens.spring.morph}>
        <button type="button" className={styles.bubblePlay} aria-label={label} onClick={toggle}>
          {playing ? <Pause size={14} strokeWidth={2} fill="currentColor" aria-hidden="true" /> : <Play size={14} strokeWidth={2} fill="currentColor" aria-hidden="true" />}
        </button>
        <span className={styles.bubbleWave} onPointerDown={seek}>
          <Bars peaks={message.peaks} progress={progress} />
        </span>
        <span className={styles.bubbleTime}>{clock(playing || now > 0 ? now : message.duration)}</span>
      </motion.div>
      {message.meta && <span className={styles.meta}>{message.meta}</span>}
      {error && <span className={styles.error} role="alert">{error}</span>}
    </div>
  </Tag>;
}

export function VoiceRecorder({ defaultMessages = [], onSend, onCancel, maxDuration, sentMeta = "Just now", label = "Voice messages", className, composerOnly = false, composerClassName, recordingMode = "demo", active = true }: VoiceRecorderProps) {
  const { policy } = useUploadPolicy(); const durationLimit = Math.min(maxDuration ?? policy.voiceRecordSeconds, policy.voiceMaxSeconds)

  const reduced = useReducedMotion() ?? false;
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const statusId = `${uid}-status`;
  const [messages, setMessages] = useState(defaultMessages);
  const [phase, setPhase] = useState<Phase>("idle");
  // Takes are numbered so the mic can wear the next take's layout id: mic, recording bar, review, and sent bubble are one shape.
  const [takeCount, setTakeCount] = useState(0);
  const [take, setTake] = useState<{ seed: number; duration: number; audioUrl?: string; blob?: Blob; peaks?: number[] }>({ seed: 1, duration: 0 });
  const [starting, setStarting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const capture = useVoiceCapture();
  const takeId = (n: number) => `${uid}-take-${n}`;
  const [status, setStatus] = useState("");
  const [shownSeconds, setShownSeconds] = useState(0);
  const elapsed = useMotionValue(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const threadRef = useRef<HTMLOListElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const phaseRef = useRef(phase);
  const startedAt = useRef(0);
  const mounted = useRef(false);
  const review = usePlayback(Math.max(.1, take.duration), take.audioUrl);
  const timer = useTransform(elapsed, ms => `${clock(ms / 1000)}.${Math.floor((ms % 1000) / 100)}`);
  const reviewTime = useTransform(review.progress, p => clock(p * take.duration));

  useEffect(() => { phaseRef.current = phase; });
  const [captureError, setCaptureError] = useState(capture.error);
  if (captureError !== capture.error) {
    setCaptureError(capture.error);
    if (capture.error) { setError(capture.error); setPhase("idle"); }
  }
  const stopAtLimit = useEffectEvent(() => { void stop(); });
  useEffect(() => {
    if (phase !== "recording" || recordingMode !== "microphone") return;
    const limit = window.setTimeout(stopAtLimit, durationLimit * 1000);
    return () => window.clearTimeout(limit);
  }, [phase, recordingMode, durationLimit]);

  // The live clock: advances the take, paints the waveform, and stops the take at the limit.
  useAnimationFrame((_, delta) => {
    if (phaseRef.current !== "recording") return;
    const next = Math.min(recordingMode === "microphone" ? performance.now() - startedAt.current : elapsed.get() + delta, durationLimit * 1000);
    elapsed.set(next);
    paint(canvasRef.current, next, take.seed, reduced, recordingMode === "microphone" ? capture.sample(next) : undefined);
    const seconds = Math.floor(next / 1000);
    if (seconds !== shownSeconds) setShownSeconds(seconds);
    if (next >= durationLimit * 1000) stop();
  });

  // Focus follows the composer: mic, then stop, then send, then back to the mic.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    primaryRef.current?.focus({ preventScroll: true });
  }, [phase]);
  // New messages scroll into view at the bottom of the thread.
  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTo({ top: thread.scrollHeight, behavior: reduced ? "auto" : "smooth" });
  }, [messages.length, reduced]);

  async function start() {
    if (starting || processing) return;
    setError("");
    if (recordingMode === "microphone") {
      setStarting(true);
      try { if (!await capture.start()) return; }
      catch (failure) {
        setError(failure instanceof DOMException && failure.name === "NotAllowedError" ? "Microphone access was denied. Allow microphone access to record." : failure instanceof Error ? failure.message : "The microphone could not start.");
        return;
      } finally { setStarting(false); }
    }
    const next = takeCount + 1;
    startedAt.current = performance.now();
    elapsed.set(0);
    setShownSeconds(0);
    setTakeCount(next);
    setTake({ seed: next * 7 + 3, duration: 0 });
    setStatus("Recording");
    phaseRef.current = "recording";
    setPhase("recording");
  }
  async function stop() {
    if (phaseRef.current !== "recording") return;
    const milliseconds = recordingMode === "microphone" ? Math.min(performance.now() - startedAt.current, durationLimit * 1000) : elapsed.get();
    const seconds = Math.max(.1, Math.round(milliseconds / 100) / 10);
    phaseRef.current = "review";
    if (recordingMode === "microphone") {
      setProcessing(true);
      try {
        const recorded = await capture.stop();
        if (!recorded) return;
        setTake(previous => ({ ...previous, ...recorded, duration: seconds }));
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : "The voice note could not be recorded.");
        phaseRef.current = "idle"; setPhase("idle");
        return;
      } finally { setProcessing(false); }
    } else {
      setTake(previous => ({ ...previous, duration: seconds }));
    }
    review.progress.set(0);
    review.setPlaying(false);
    setStatus(`Recorded ${clock(seconds)}. Play it back or send it.`);
    setPhase("review");
  }
  const deactivateRecording = useEffectEvent(() => {
    review.setPlaying(false);
    if (phase === "recording") void stop();
  });
  useEffect(() => { if (!active) deactivateRecording(); }, [active, phase]);
  function discard() {
    capture.cancel();
    phaseRef.current = "idle";
    review.setPlaying(false);
    // Hand the id back so the bar folds into the mic it came from.
    setTakeCount(count => count - 1);
    setStatus("Voice note discarded");
    setPhase("idle");
    onCancel?.();
  }
  async function send() {
    if (processing || (recordingMode === "microphone" && !take.blob)) return;
    const message: VoiceMessage = { id: takeId(takeCount), duration: take.duration, peaks: take.peaks ?? voicePeaks(take.seed, take.duration), meta: sentMeta, audioUrl: take.audioUrl, blob: take.blob };
    review.setPlaying(false);
    setProcessing(true);
    try {
      await onSend?.(message);
      setMessages(previous => [...previous, message]);
      setStatus(composerOnly ? "Voice note added to chat" : "Voice note sent");
      setPhase("idle");
    } catch (error) { setStatus(error instanceof Error ? error.message : "The recording was not sent. Try again."); }
    finally { setProcessing(false); }
  }
  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape" && phase !== "idle") { event.preventDefault(); event.stopPropagation(); discard(); }
  }

  const swap = { initial: { opacity: 0, filter: `blur(${motionTokens.blur.subtle}px)` }, animate: { opacity: 1, filter: "blur(0px)", transition: { duration: motionTokens.duration.standard, delay: reduced ? 0 : .05 } }, exit: { opacity: 0, filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.instant } } };
  const sideButton = { initial: { opacity: 0, scale: .7 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: .7, transition: { duration: motionTokens.duration.instant } }, transition: motionTokens.spring.snappy };

  return <MotionConfig reducedMotion="user">
    <section className={[styles.root, className].filter(Boolean).join(" ")} aria-label={label}>
      <LayoutGroup>
        {!composerOnly && <ol ref={threadRef} className={styles.thread} aria-label="Messages">
          {messages.map(message => <VoiceMessageBubble key={message.id} message={message} layoutId={`vr-${message.id}`} />)}
        </ol>}

        <div className={[styles.composer, composerClassName].filter(Boolean).join(" ")} data-phase={phase} onKeyDown={onKeyDown}>
          <AnimatePresence initial={false} mode="popLayout">
            {phase === "idle" && <motion.span key="hint" className={styles.hint} {...swap}>Tap the mic to record a voice note</motion.span>}
          </AnimatePresence>

          <AnimatePresence initial={false} mode="popLayout">
            {phase !== "idle" && <motion.button key="discard" type="button" className={styles.discard} aria-label="Discard voice note" onClick={discard} {...sideButton}>
              <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
            </motion.button>}
          </AnimatePresence>

          {phase === "idle"
            ? <motion.button key="mic" ref={primaryRef} layoutId={`vr-${takeId(takeCount + 1)}`} type="button" className={styles.mic} aria-label={starting ? "Starting microphone" : "Record voice note"} aria-describedby={statusId} disabled={starting} onClick={() => void start()}
                initial={{ opacity: 0, scale: .8 }} animate={{ opacity: 1, scale: 1 }} style={{ borderRadius: 999 }} transition={motionTokens.spring.morph}>
                <Mic size={20} strokeWidth={1.75} aria-hidden="true" />
              </motion.button>
            : <motion.div layoutId={`vr-${takeId(takeCount)}`} className={styles.capsule} data-phase={phase} style={{ borderRadius: 999 }} transition={motionTokens.spring.morph}>
                <AnimatePresence initial={false} mode="popLayout">
                  {phase === "recording"
                    ? <motion.div key="recording" className={styles.capsuleRow} {...swap}>
                        <span className={styles.recDot} aria-hidden="true" />
                        <motion.span className={styles.timer} aria-hidden="true">{timer}</motion.span>
                        <canvas ref={canvasRef} className={styles.live} aria-hidden="true" />
                      </motion.div>
                    : <motion.div key="review" className={styles.capsuleRow} {...swap}>
                        <button type="button" className={styles.reviewPlay} aria-label={review.playing ? "Pause preview" : "Play preview"} onClick={review.toggle}>
                          {review.playing ? <Pause size={14} strokeWidth={2} fill="currentColor" aria-hidden="true" /> : <Play size={14} strokeWidth={2} fill="currentColor" aria-hidden="true" />}
                        </button>
                        <span className={styles.reviewWave} onPointerDown={event => { const rect = event.currentTarget.getBoundingClientRect(); review.seek(clamp((event.clientX - rect.left) / rect.width, 0, 1)); }}>
                          <Bars peaks={take.peaks ?? voicePeaks(take.seed, take.duration, 40)} progress={review.progress} />
                        </span>
                        <motion.span className={styles.timer} aria-hidden="true">{review.playing ? reviewTime : clock(take.duration)}</motion.span>
                      </motion.div>}
                </AnimatePresence>
              </motion.div>}

          <AnimatePresence initial={false} mode="popLayout">
            {phase === "recording" && <motion.button key="stop" ref={primaryRef} type="button" className={styles.stop} aria-label={`Stop recording at ${clock(shownSeconds)}`} disabled={processing} onClick={() => void stop()} {...sideButton}>
              <Square size={14} strokeWidth={2} fill="currentColor" aria-hidden="true" />
            </motion.button>}
            {phase === "review" && <motion.button key="send" ref={primaryRef} type="button" className={styles.send} aria-label="Send voice note" onClick={send} {...sideButton}>
              <ArrowUp size={18} strokeWidth={2} aria-hidden="true" />
            </motion.button>}
          </AnimatePresence>
        </div>
      </LayoutGroup>
      {(error || review.error) && <p className={styles.error} role="alert">{error || review.error}</p>}
      <p id={statusId} className={styles.srOnly} aria-live="polite">{status}</p>
    </section>
  </MotionConfig>;
}

export default VoiceRecorder;
